import React, { useMemo, useState } from 'react';
import { ChevronDown, Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api.js';
import { fmtTime, parseTime } from '../../lib/time.js';
import { sanitizeShape } from '../../lib/sessionState.js';
import { useSessionState } from '../../lib/useSessionState.js';
import { countActiveFilters } from '../../lib/listFilters.js';
import { TimeField } from '../../components/ui.jsx';
import { Select } from '../../components/controls/Select.jsx';
import { DatePicker } from '../../components/controls/DatePicker.jsx';
import { ResetFiltersButton } from '../../components/controls/ResetFiltersButton.jsx';

const SOURCES = ['Email', 'Meeting', 'Chat', 'Manual note'];
// Written automatically through MCP (e.g. a HubSpot or Excel reconciliation).
// Never offered in the form, but filterable, marked apart in the list, and an
// edit keeps it — the API refuses to create one or turn an entry into one.
const IMPORT_SOURCE = 'Import';
const VISIBILITIES = ['Internal', 'External'];
const SOURCE_FILTERS = ['All', ...SOURCES, IMPORT_SOURCE];
const VISIBILITY_FILTERS = ['All', ...VISIBILITIES];
const HISTORY_FILTERS = { source: 'All', visibility: 'All', search: '' };

function sanitizeHistoryFilters(value, fallback) {
  const shaped = sanitizeShape(value, fallback);
  return {
    ...shaped,
    source: SOURCE_FILTERS.includes(shaped.source) ? shaped.source : 'All',
    visibility: VISIBILITY_FILTERS.includes(shaped.visibility)
      ? shaped.visibility
      : 'All',
  };
}

function fmtWhen(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// A local datetime-local value ("YYYY-MM-DDTHH:mm") for an ISO string / now.
function toLocalInput(iso) {
  const date = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const splitLines = (value) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

export function ProjectHistoryTab({ project, detail, loading, reloadDetail }) {
  const [filters, setFilters] = useSessionState(
    `project.${project.id}.history`,
    HISTORY_FILTERS,
    sanitizeHistoryFilters,
  );
  const {
    source: sourceFilter,
    visibility: visibilityFilter,
    search,
  } = filters;
  const setFilter = (key) => (value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const setSourceFilter = setFilter('source');
  const setVisibilityFilter = setFilter('visibility');
  const setSearch = setFilter('search');
  const [expanded, setExpanded] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);

  const visible = useMemo(() => {
    const needle = search.toLowerCase();
    return detail.interactions.filter((item) => {
      if (sourceFilter !== 'All' && item.source !== sourceFilter) return false;
      if (visibilityFilter !== 'All' && item.visibility !== visibilityFilter)
        return false;
      return `${item.title} ${item.summary} ${item.participants}`
        .toLowerCase()
        .includes(needle);
    });
  }, [detail.interactions, sourceFilter, visibilityFilter, search]);

  async function deleteInteraction(id) {
    await api(`/projects/${project.id}/interactions/${id}`, {
      method: 'DELETE',
    });
    setConfirmingId(null);
    await reloadDetail();
  }

  return (
    <div className="history-tab">
      <div className="tab-toolbar">
        <p className="tab-intro">
          Chronological record of meetings, emails, chats and notes.
        </p>
        <div className="tab-toolbar-actions">
          <input
            className="tab-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search history"
          />
          <ResetFiltersButton
            count={countActiveFilters(filters, HISTORY_FILTERS)}
            onReset={() => setFilters(HISTORY_FILTERS)}
          />
          <button
            type="button"
            className="secondary small"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
            }}
            disabled={adding}
          >
            <Plus size={14} /> Add interaction
          </button>
        </div>
      </div>

      <div className="history-layout">
        <aside className="history-filters card">
          <b>Source</b>
          {SOURCE_FILTERS.map((value) => (
            <label key={value} className="check">
              <input
                type="radio"
                checked={sourceFilter === value}
                onChange={() => setSourceFilter(value)}
              />
              {value}
            </label>
          ))}
          <hr />
          <b>Visibility</b>
          {VISIBILITY_FILTERS.map((value) => (
            <label key={value} className="check">
              <input
                type="radio"
                checked={visibilityFilter === value}
                onChange={() => setVisibilityFilter(value)}
              />
              {value}
            </label>
          ))}
          <hr />
          <small className="muted-text">
            {loading
              ? 'Loading…'
              : `Showing ${visible.length} of ${detail.interactions.length}`}
          </small>
        </aside>

        <section className="history-list">
          {adding && (
            <article className="history-event is-editing">
              <div className="history-event-heading">New interaction</div>
              <InteractionForm
                onCancel={() => setAdding(false)}
                onSaved={async () => {
                  setAdding(false);
                  await reloadDetail();
                }}
                submit={(payload) =>
                  api(`/projects/${project.id}/interactions`, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                  })
                }
              />
            </article>
          )}

          {!loading && !adding && detail.interactions.length === 0 && (
            <div className="empty-panel">
              No interactions recorded yet. Add meetings, emails, chats or notes
              that matter for this project.
            </div>
          )}
          {!loading &&
            detail.interactions.length > 0 &&
            visible.length === 0 && (
              <div className="empty-panel">
                No interactions match the current filters.
              </div>
            )}

          {visible.map((item) => {
            const open = expanded === item.id;

            if (editingId === item.id) {
              return (
                <article className="history-event is-editing" key={item.id}>
                  <div className="history-event-heading">Edit interaction</div>
                  <InteractionForm
                    entry={item}
                    onCancel={() => setEditingId(null)}
                    onSaved={async () => {
                      setEditingId(null);
                      await reloadDetail();
                    }}
                    submit={(payload) =>
                      api(`/projects/${project.id}/interactions/${item.id}`, {
                        method: 'PUT',
                        body: JSON.stringify(payload),
                      })
                    }
                  />
                </article>
              );
            }

            return (
              <article
                className={`history-event${open ? ' expanded' : ''}`}
                key={item.id}
              >
                <div className="history-event-row">
                  <button
                    className="history-event-top"
                    onClick={() => setExpanded(open ? null : item.id)}
                  >
                    {item.source === IMPORT_SOURCE ? (
                      <span
                        className="history-marker import"
                        aria-label="Automatic import"
                      >
                        <Download size={14} aria-hidden="true" />
                      </span>
                    ) : (
                      <span className="history-marker">{item.source[0]}</span>
                    )}
                    <div className="history-event-body">
                      <div className="history-event-meta">
                        <small>
                          {fmtWhen(item.occurredAt)} · {item.source}
                        </small>
                        <span
                          className={`project-status-badge${item.visibility === 'External' ? ' status-active' : ''}`}
                        >
                          {item.visibility}
                        </span>
                        {item.source === IMPORT_SOURCE && (
                          <span className="history-import-badge">
                            Automatic import
                          </span>
                        )}
                      </div>
                      <h3>{item.title}</h3>
                      {item.summary && <p>{item.summary}</p>}
                      {item.sourceRef && (
                        <span className="history-source">
                          Source: {item.sourceRef}
                        </span>
                      )}
                    </div>
                    <ChevronDown size={16} className="history-chevron" />
                  </button>

                  {confirmingId === item.id ? (
                    <div className="editable-list-confirm">
                      <span>Delete?</span>
                      <button
                        type="button"
                        className="text-button danger-text"
                        onClick={() => deleteInteraction(item.id)}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => setConfirmingId(null)}
                      >
                        Keep
                      </button>
                    </div>
                  ) : (
                    <div className="editable-list-item-actions">
                      <button
                        type="button"
                        className="icon-button"
                        aria-label="Edit"
                        onClick={() => {
                          setEditingId(item.id);
                          setAdding(false);
                        }}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        aria-label="Delete"
                        onClick={() => setConfirmingId(item.id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>

                {open && (
                  <div className="history-event-details">
                    {item.participants && (
                      <div>
                        <b>Participants</b>
                        <p>{item.participants}</p>
                      </div>
                    )}
                    {item.decisions.length > 0 && (
                      <div>
                        <b>Decisions</b>
                        {item.decisions.map((line, i) => (
                          <p key={i}>• {line}</p>
                        ))}
                      </div>
                    )}
                    {item.actions.length > 0 && (
                      <div>
                        <b>Action items</b>
                        {item.actions.map((line, i) => (
                          <p key={i}>• {line}</p>
                        ))}
                      </div>
                    )}
                    {item.createdByName && (
                      <small className="muted-text">
                        Added by {item.createdByName}
                      </small>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}

function InteractionForm({ entry, onCancel, onSaved, submit }) {
  const [form, setForm] = useState({
    occurredAt: toLocalInput(entry?.occurredAt),
    source: entry?.source || 'Manual note',
    visibility: entry?.visibility || 'Internal',
    title: entry?.title || '',
    summary: entry?.summary || '',
    participants: entry?.participants || '',
    decisions: (entry?.decisions || []).join('\n'),
    actions: (entry?.actions || []).join('\n'),
    sourceRef: entry?.sourceRef || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await submit({
        occurredAt: new Date(form.occurredAt).toISOString(),
        source: form.source,
        visibility: form.visibility,
        title: form.title,
        summary: form.summary,
        participants: form.participants,
        decisions: splitLines(form.decisions),
        actions: splitLines(form.actions),
        sourceRef: form.sourceRef,
      });
      await onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form className="interaction-form" onSubmit={save}>
      <div className="form-grid">
        <div className="datetime-field">
          <span className="modal-label">Date &amp; time</span>
          <div className="datetime-field-controls">
            <DatePicker
              ariaLabel="Date"
              required
              value={form.occurredAt.slice(0, 10)}
              onChange={(date) =>
                set({ occurredAt: `${date}${form.occurredAt.slice(10)}` })
              }
            />
            <TimeField
              value={parseTime(form.occurredAt.slice(11, 16))}
              onChange={(hours) =>
                set({
                  occurredAt: `${form.occurredAt.slice(0, 10)}T${fmtTime(
                    Math.min(hours, 23 + 59 / 60),
                  )}`,
                })
              }
            />
          </div>
        </div>
        <label>
          <span>Source</span>
          {form.source === IMPORT_SOURCE ? (
            <input value="Import (automatic)" readOnly disabled />
          ) : (
            <Select
              ariaLabel="Source"
              value={form.source}
              onChange={(source) => set({ source })}
              options={SOURCES.map((option) => ({
                value: option,
                label: option,
              }))}
            />
          )}
        </label>
        <label>
          <span>Visibility</span>
          <Select
            ariaLabel="Visibility"
            value={form.visibility}
            onChange={(visibility) => set({ visibility })}
            options={VISIBILITIES.map((option) => ({
              value: option,
              label: option,
            }))}
          />
        </label>
        <label>
          <span>Title</span>
          <input
            required
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </label>
      </div>
      <label className="full">
        <span>Summary</span>
        <textarea
          value={form.summary}
          onChange={(e) => set({ summary: e.target.value })}
          placeholder="The meaningful outcome of the interaction"
        />
      </label>
      <label className="full">
        <span>Participants</span>
        <input
          value={form.participants}
          onChange={(e) => set({ participants: e.target.value })}
          placeholder="Names, comma-separated"
        />
      </label>
      <div className="form-grid">
        <label>
          <span>Decisions (one per line)</span>
          <textarea
            value={form.decisions}
            onChange={(e) => set({ decisions: e.target.value })}
          />
        </label>
        <label>
          <span>Action items (one per line)</span>
          <textarea
            value={form.actions}
            onChange={(e) => set({ actions: e.target.value })}
          />
        </label>
      </div>
      <label className="full">
        <span>Source link / reference</span>
        <input
          value={form.sourceRef}
          onChange={(e) => set({ sourceRef: e.target.value })}
          placeholder="Outlook, Teams meeting, URL…"
        />
      </label>
      {error && <div className="error">{error}</div>}
      <div className="inline-form-actions">
        <button
          type="button"
          className="text-button"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button className="primary small" type="submit" disabled={saving}>
          {saving ? 'Saving…' : entry ? 'Save' : 'Add interaction'}
        </button>
      </div>
    </form>
  );
}
